import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import type { UserProfileDetails } from 'src/replicant/friends/friends.asyncGetters';
import { Feature } from 'src/replicant/analytics';
import { getPetName } from 'src/replicant/pet/pet.getters';
import { user } from 'src/StateObserver';
import { AssetGroup } from '@play-co/timestep-core';
import { FlexBoxView, getFlexNode } from '@play-co/timestep-core/flexbox';
import { FriendListItem } from '../components/FriendListItem';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { ImageView } from '@play-co/timestep-core/ui';

const assets = {
  dashedLine: 'assets/ui/shared/frames/dashed_line.png',
};

type CloseHandler = (success: boolean) => void;

export default class PopupFriendRemoveConfirm extends PopupBasic {
  private content: FlexBoxView;
  private player: FriendListItem;

  protected getAssetsGroup() {
    return new AssetGroup(
      AssetGroup.constructURLs(['assets/ui/friends', 'assets/ui/shared']),
    );
  }

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      zIndex: 10000,
      width: uiConfig.popups.buruburu.width,
      height: 740,
      darkerBg: false,
      close: () => opts.close(false),
      tracking: {
        feature: Feature.FRIENDS._,
        $subFeature: Feature.FRIENDS.POPUP_CONFIRM_REMOVE._,
      },
    });

    // Remove default close button
    this.buttonClose.removeFromSuperview();

    // Make the player item width shorter so we can scale it up without changing it's height
    // That's to preserve inner content appearance and spacing
    this.player = new FriendListItem({
      superview: this.box,
      x: this.box.style.width / 2,
      y: 170,
      centerOnOrigin: true,
      width: 380,
      scale: 1.33,
    });

    // Create content wrapper container
    this.content = new FlexBoxView({
      superview: this.box,
      flex: {
        width: this.box.style.width,
        height: this.box.style.height,
        flexDirection: 'column',
        justifyContent: 'flex-end',
        alignItems: 'center',
        paddingBottom: 40,
      },
    });

    this.message.updateOpts({
      superview: this.content,
      size: 24,
      width: this.box.style.width - 50,
      height: 150,
    });

    // Create dashed separator
    new ImageView({
      superview: this.content,
      image: assets.dashedLine,
      width: 389,
      height: 10,
    });

    new LangBitmapFontTextView({
      superview: this.content,
      size: 30,
      font: 'Body',
      color: '#9A7171',
      verticalAlign: 'top',
      x: 105,
      y: 25,
      offsetY: 6,
      width: 200,
      height: 48,
      localeText: () => i18n('friendRemoveConfirm.areYouSure'),
    });

    // Create keep friend button
    new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      superview: this.content,
      labelOffsetX: -5,
      labelOffsetY: -1,
      fontSize: 46,
      font: 'Body',
      width: 400,
      height: 100,
      disabledFontOffsetY: -1,
      localeText: () => i18n('friendRemoveConfirm.keep'),
      onClick: async () => {
        sounds.sfx.play('ok');
        return opts.close(false);
      },
      tracking: {
        feature: Feature.FRIENDS._,
        $subFeature: Feature.FRIENDS.POPUP_CONFIRM_REMOVE.KEEP,
      },
    });

    // Create unfriend button
    new ButtonScaleViewWithText({
      ...uiConfig.buttons.cancel,
      superview: this.content,
      labelOffsetX: -5,
      labelOffsetY: -1,
      fontSize: 32,
      font: 'Body',
      width: 230,
      height: 80,
      localeText: () => i18n('friendRemoveConfirm.remove'),
      onClick: async () => {
        sounds.sfx.play('cancel');
        return opts.close(true);
      },
      tracking: {
        feature: Feature.FRIENDS._,
        $subFeature: Feature.FRIENDS.POPUP_CONFIRM_REMOVE.REMOVE,
      },
    });

    // Set margins for inner items in flex content
    // to keep them spaced out equally
    for (const child of this.content.getSubviews()) {
      if (child) {
        getFlexNode(child).updateStyle({
          marginBottom: 10,
        });
      }
    }
  }

  public init({ details }: { details: UserProfileDetails }) {
    const userPetName = getPetName(user());
    const friendPetName = details.attributes.name;

    super.init({
      title: i18n('friendRemoveConfirm.title'),
      message: i18n('friendRemoveConfirm.message', {
        userPetName,
        friendPetName,
      }),
    });

    // Setup friend item with player data (no callbacks = no buttons shown)
    this.player.setup({
      id: details.id,
      name: details.name,
      profilePicture: details.photo,
      frame: details.frame,
      species: details.currentEvolution.species,
      petName: details.attributes.name,
    });
  }
}
